// useVisualViewportPin — May 11, 2026
//
// Keeps an element pinned to the visual viewport so it stays visible at its
// original size and position even when the user pinch-zooms the page.
//
// Why this exists: even with `maximum-scale=1, user-scalable=no` in the
// viewport meta, iOS Safari can still pinch-zoom the page when the user has
// "Allow scaling" or system Accessibility Zoom enabled, or when a Mapbox
// gesture is incorrectly interpreted as a page-level zoom. When that
// happens, the app header anchored to the LAYOUT viewport's top-left ends
// up off-screen as the user pans the zoomed view around.
//
// The fix: the VisualViewport API exposes the user's actual visible area
// (`offsetLeft`, `offsetTop`, `scale`) along with scroll/resize events that
// fire on pinch-zoom + pan. We translate the element by the visual viewport
// offset and inverse-scale it by `1 / scale`, which counteracts the page
// zoom — the header floats back to the visible top-left at its original
// pixel size.
//
// The element should be a layout child (position: static, relative, fixed,
// or sticky). We apply the transform via inline style; the element's normal
// layout flow is preserved when the visual viewport hasn't been zoomed or
// panned (offset = 0, scale = 1, transform is a no-op).

import { useEffect, type RefObject } from "react";

export function useVisualViewportPin(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return; // Very old browsers — graceful degrade, no pinning.
    const el = ref.current;
    if (!el) return;
    // Sep 25, 2026: touch devices only. On a Mac, a trackpad pinch in Safari
    // also moves the visual viewport, and the translated header then slid
    // down over the desktop nav tab row (Home / Dispatch / … / More), hiding
    // it until the user reset zoom (Jennifer, "can't see the dispatch
    // board"). Desktop has no keyboard/notch problem to solve, so skip it.
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    // Anchor the transform at the element's top-left so translate +
    // inverse-scale together park the element at visual viewport (0,0).
    el.style.transformOrigin = "0 0";
    el.style.willChange = "transform";

    let rafId = 0;
    let lastTransform = "";

    const update = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const offsetX = vv.offsetLeft;
        const offsetY = vv.offsetTop;
        const scale = vv.scale;
        const inv = 1 / scale;
        // Round to whole pixels to avoid GPU sub-pixel jitter on iOS.
        const tx = Math.round(offsetX);
        const ty = Math.round(offsetY);
        const next =
          scale === 1 && tx === 0 && ty === 0
            ? "" // No transform when the visual viewport matches the layout
            : `translate3d(${tx}px, ${ty}px, 0) scale(${inv})`;
        if (next !== lastTransform) {
          el.style.transform = next;
          lastTransform = next;
        }
      });
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      cancelAnimationFrame(rafId);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      // Don't reset the inline styles on unmount — the element might be
      // re-mounted with the same ref and we want continuity.
    };
  }, [ref]);
}
